import { useContext, useEffect, useReducer, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { useFarmController } from "components/Vault/Farm/useFarmController";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { useSnackbar } from "notistack";

import { ReportsIcon } from "../../Icons/Icons";
import { formatUnitNumber, fsaSort } from "./helpers";
import { ReportViewer } from "./ReportViewer";

export default function FarmTractFieldReport() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const { enqueueSnackbar } = useSnackbar();
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  const [currentYear, setCurrentYear] = useState(
    globalState?.crop_year ? globalState?.crop_year : getCurrentReinsuranceYear()
  );
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: [],
    practice_name: [],
  });

  const [isDetailViewOn, setIsDetailViewOn] = useState(false);

  const setIsDetailViewOnFunc = (val) => {
    setIsDetailViewOn(val);
  };

  const { counties, isLoadingCounties } = useFarmController({
    crop_year: currentYear,
    filter: filter,
    onError: handleError,
  });

  const columnsDirective = [
    { field: "location_county_name", headerText: "County", width: 100 },
    { field: "location_state_name", headerText: "State", width: 100 },
    { field: "fsa_farm_number", headerText: "Farm Number", width: 100, textAlign: "Right", sortComparer: fsaSort },
    { field: "fsa_tract_number", headerText: "Track", width: 100, textAlign: "Right", sortComparer: fsaSort },
    { field: "fsa_field_number", headerText: "Field", width: 100, textAlign: "Right", sortComparer: fsaSort },
    { field: "crop_year", headerText: "Year", width: 100, textAlign: "Right", visible: isDetailViewOn },
    { field: "reported_acreage", headerText: "Reported Acreage", width: 100, textAlign: "Right", format: "N2" },
    { field: "share_percent", headerText: "Share Percent", width: 100, textAlign: "Right", format: "P0" },
    { field: "commodity_name", headerText: "Commodity", width: 100 },
    { field: "type_name", headerText: "Type", width: 100, visible: isDetailViewOn },
    { field: "practice_name", headerText: "Practice", width: 100 },
    { field: "approved_yield", headerText: "Approved Yield", width: 100, textAlign: "Right", format: "N0" },
    { field: "rate_yield", headerText: "Rate Yield", width: 100, textAlign: "Right", format: "N2" },
    {
      field: "lastupdate",
      headerText: "Last Update",
      width: 100,
      textAlign: "Right",
      type: "date",
      format: "yMd",
      visible: isDetailViewOn,
    },
    {
      field: "adjusted_yield",
      headerText: "Adjusted Yield",
      width: 100,
      textAlign: "Right",
      format: "N0",
      valueAccessor: (field, data) => {
        return data[field] || "";
      },
    },
    {
      field: "average_yield",
      headerText: "Average Yield",
      width: 100,
      textAlign: "Right",
      format: "N0",
      valueAccessor: (field, data) => {
        return data[field] || "";
      },
    },
    {
      field: "planted_date",
      headerText: "Plant Date",
      width: 100,
      textAlign: "Right",
      type: "date",
      format: "yMd",
    },
    { field: "unit_number", headerText: "Unit #", width: 100 },
    { field: "other_person_sharing", headerText: "Share Owner Name", width: 100 },
  ];

  const transformFieldData = (originalData) => {
    if (!Array.isArray(originalData)) return []; // Ensure valid input

    return originalData.flatMap(
      (county) =>
        county?.ProducerFarms?.flatMap((producerFarm) =>
          producerFarm?.ProducerFarmFieldCrops?.map((producerFarmFieldCrop) => ({
            ...producerFarmFieldCrop,
            CustomerName: producerFarm?.customer_name || "",
            OrderDate: producerFarm?.order_date || null,
            Freight: producerFarm?.freight || 0,
            ShippedDate: producerFarm?.shipped_date || null,
            reported_acreage: producerFarmFieldCrop?.reported_acreage,
            share_percent: producerFarmFieldCrop?.share_percent,
            rate_yield: producerFarmFieldCrop?.rate_yield,
            adjusted_yield: producerFarmFieldCrop?.adjusted_yield,
            average_yield: producerFarmFieldCrop?.average_yield,
            unit_number: formatUnitNumber(
              producerFarmFieldCrop?.basic_unit_number,
              producerFarmFieldCrop?.optional_unit_number
            ),
            other_person_sharing: producerFarm?.other_person_sharing,
          }))
        ) || []
    );
  };

  return (
    <>
      <WorkspaceHeader
        title="Planted Acres by Field"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />

      <ReportViewer
        columnsDirective={columnsDirective}
        counties={counties}
        producerFarmFieldCrops={transformFieldData(counties)}
        isLoadingCounties={isLoadingCounties}
        reportType="field"
        setIsDetailViewOnFunc={setIsDetailViewOnFunc}
        currentYear={currentYear}
        setCurrentYear={setCurrentYear}
        gridKey="FieldReport"
        filter={filter}
        dispatchFilter={dispatchFilter}
      />

      {!isLoadingCounties && !counties?.length && (
        <>
          <EmptyCard text="No search results" secondaryText="Remove some filters or add a new farm in the Farm Vault" />
        </>
      )}
    </>
  );
}
